import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import MainLayout from "@/components/layouts/MainLayout";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Search, DollarSign, Percent } from "lucide-react";
import { Sale } from "@/models/sale";
import { Ad } from "@/models/ad";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const MySales = () => {
  const { user } = useAuth();
  const { sales, screens, ads, expiredAds } = useData();
  const [filteredSales, setFilteredSales] = useState<Sale[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<string>("all");
  
  const years = [...new Set(sales.map(sale => new Date(sale.date).getFullYear()))].sort().reverse();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Obtener el perfil del usuario para obtener su porcentaje de comisión
  const { data: userProfile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Filter sales that belong to the current user
  useEffect(() => {
    if (sales && user) {
      let userSales = sales.filter(sale => sale.sellerId === user.id);

      // Apply search filter
      if (searchTerm) {
        userSales = userSales.filter(sale => 
          sale.clientName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Apply year filter
      if (selectedYear !== "all") {
        const year = parseInt(selectedYear);
        userSales = userSales.filter(sale => new Date(sale.date).getFullYear() === year);
      }

      // Apply month filter if a year is selected
      if (selectedMonth !== "all" && selectedYear !== "all") {
        const month = parseInt(selectedMonth);
        userSales = userSales.filter(sale => new Date(sale.date).getMonth() + 1 === month);
      }
      
      setFilteredSales(userSales);
    }
  }, [sales, user, searchTerm, selectedYear, selectedMonth]);

  // Calculate total sales amount
  const totalSalesAmount = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);

  // Calculate total commission earnings using dynamic commission percentages
  const totalCommissionEarnings = filteredSales.reduce((sum, sale) => {
    const saleCommission = (sale.amount * (sale.commissionPercentage || 0)) / 100;
    return sum + saleCommission;
  }, 0);

  // Calculate average commission percentage for display (optional)
  const averageCommissionPercentage = filteredSales.length > 0 
    ? filteredSales.reduce((sum, sale) => sum + (sale.commissionPercentage || 0), 0) / filteredSales.length
    : 0;

  // Function to get ad by ID from both active and expired ads
  const getAdById = (adId: string): Ad | undefined => {
    return [...ads, ...expiredAds].find(ad => ad.id === adId);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="h-8 w-8 text-outdoor-600" />
            Mis Ventas
          </h1>
          <p className="text-muted-foreground">
            Historial de todas las ventas realizadas
          </p>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por nombre del anuncio..." 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              className="pl-10" 
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={selectedMonth} 
              onValueChange={setSelectedMonth} 
              disabled={selectedYear === "all"}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Mes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {months.map(month => (
                  <SelectItem key={month} value={month.toString()}>
                    {format(new Date(2022, month - 1), 'MMMM', { locale: es })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sales and Commission Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Sales Total Card */}
          <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-white" />
                Total de Ventas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                ${totalSalesAmount.toLocaleString()}
              </div>
              <div className="text-sm text-blue-100 mt-1">
                {filteredSales.length} anuncios vendidos
              </div>
            </CardContent>
          </Card>

          {/* Commission Earnings Card */}
          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
                <Percent className="h-5 w-5 text-white" />
                Mis Ganancias
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                ${totalCommissionEarnings.toLocaleString()}
              </div>
              <div className="text-sm text-emerald-100 mt-1">
                Comisión sobre ventas realizadas
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Sales List */}
        {filteredSales.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSales.map(sale => (
              <SaleCard 
                key={sale.id} 
                sale={sale} 
                ad={getAdById(sale.adId)}
                commissionPercentage={averageCommissionPercentage}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">No se encontraron ventas con los filtros seleccionados</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

// Sale Card Component
const SaleCard = ({ sale, ad, commissionPercentage }: { sale: Sale; ad?: Ad; commissionPercentage: number }) => {
  // Usar el porcentaje específico de esta venta, no el promedio general
  const saleCommissionPercentage = sale.commissionPercentage || 0;
  const commissionEarnings = (sale.amount * saleCommissionPercentage) / 100;
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-medium">{sale.clientName}</h3>
          <div className="text-right">
            <div className="text-lg font-bold text-outdoor-600">${sale.amount.toLocaleString()}</div>
            {saleCommissionPercentage > 0 && (
              <div className="text-sm font-medium text-green-600">
                +${commissionEarnings.toLocaleString()}
              </div>
            )}
          </div>
        </div>
        
        <div className="text-sm text-muted-foreground mb-3">
          Fecha: {format(new Date(sale.date), 'PPP', { locale: es })}
        </div>
        
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase mb-1">
            {ad && ad.screens.length > 1 ? 'Pantallas' : 'Pantalla'}
          </div>
          <div className="flex flex-wrap gap-2">
            {ad && ad.screens.length > 0 ? (
              ad.screens.map((screen, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="bg-blue-100 text-blue-800 border-blue-200"
                >
                  {screen.screenName}
                </Badge>
              ))
            ) : (
              <Badge 
                variant="outline" 
                className="bg-blue-100 text-blue-800 border-blue-200"
              >
                {sale.screenName}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MySales;
